import {
  AfterViewChecked,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownComponent } from 'ngx-markdown';
import { ChatService } from '../../_services/chat.service';
import { WorkflowService } from '../../_services/workflow.service';
import { ChatCapabilityGroup, ChatUiMessage } from '../../_models/chat.model';
import { Waiting } from '../waiting/waiting';
import { RerunTask } from '../rerun-task/rerun-task';
import { InlineSnackbar } from '../inline-snackbar/inline-snackbar';
import { SnackbarType } from '../../_enums/snackbar-type.enum';

@Component({
  selector: 'app-chat',
  imports: [
    TranslateModule,
    FormsModule,
    MatButton,
    MatIconModule,
    MarkdownComponent,
    Waiting,
    RerunTask,
    InlineSnackbar,
  ],
  templateUrl: './chat.html',
  styleUrl: './chat.scss',
})
export class Chat implements OnInit, AfterViewChecked {
  @ViewChild('feed') feedEl: ElementRef<HTMLElement> | undefined;

  public input = '';
  public isStatusLoading = true;
  public statusError = false;
  public configured = false;
  public capabilities: ChatCapabilityGroup[] = [];

  public readonly SnackbarType = SnackbarType;

  private lastFeedItemCount = -1;

  private readonly chatService = inject(ChatService);
  private readonly workflowService = inject(WorkflowService);

  // The conversation state lives in ChatService, so it survives switching steps.
  get messages(): ChatUiMessage[] {
    return this.chatService.messages;
  }

  get isSending(): boolean {
    return this.chatService.isSending;
  }

  get isSendDisabled(): boolean {
    return this.isSending || this.input.trim().length === 0;
  }

  public ngOnInit(): void {
    this.loadStatus();
  }

  public loadStatus(): void {
    this.isStatusLoading = true;
    this.statusError = false;
    this.chatService.getStatus().subscribe({
      next: (status) => {
        this.configured = status.configured;
        this.capabilities = status.capabilities;
        this.chatService.setToolLabels(
          Object.fromEntries(
            status.capabilities.flatMap((group) =>
              group.items.map((item) => [item.name, item.label]),
            ),
          ),
        );
        this.isStatusLoading = false;
      },
      error: () => {
        this.isStatusLoading = false;
        this.statusError = true;
      },
    });
  }

  public ngAfterViewChecked(): void {
    const feedItemCount = this.messages.length + (this.isSending ? 1 : 0);
    if (this.feedEl && feedItemCount !== this.lastFeedItemCount) {
      this.feedEl.nativeElement.scrollTop = this.feedEl.nativeElement.scrollHeight;
      this.lastFeedItemCount = feedItemCount;
    }
  }

  public onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  public send(): void {
    const question = this.input.trim();
    if (!question || this.isSending) {
      return;
    }

    this.workflowService.setStepInProgress(true);
    this.input = '';
    this.chatService.send(question);
  }
}
